<template>
  <div class="options-container">
    <div class="options-head">
      <div class="options-heading drag-wrapper"/>
      <div class="options-heading button-wrapper"/>
      <div class="options-heading label-wrapper">
        <span class="caption">
          {{ $t('ui.drag_editor.active_item.label') }}
        </span>
      </div>
      <div class="options-heading value-wrapper">
        <span class="caption">
          {{ $t('ui.drag_editor.active_item.value') }}
        </span>
      </div>
      <div class="options-heading delete"/>
    </div>
    <drag-container
      groupName="optionsDrag"
      :collection="internalCollection"
      :shouldRemoveOnDrop="false"
      :shouldAcceptDrops="true"
      nonDragAreaSelector="input"
      @onDrop="onDrop"
      :dropPlaceholderOptions="{className: 'drag-ghost-options'}"
      dragClass="drag-dragging-options"
      lockAxis="y"
      ref="container"
      behaviour="contain"
    >
      <Draggable
        v-for="(item, idx) in internalCollection"
        class="drag-item option-row"
        :key="idx"
      >
        <div class="options-table-content">
          <div class=" drag-wrapper options-drag-icon">
            <img class="drag-icon" :src="dragIcon">
          </div>
          <div class=" button-wrapper" v-if="element_type != 'quantity_selection'">
            <input
              type="checkbox"
              v-model="internalDefaultValue"
              :value="item.value"
              :true-value="item.value"
              :false-value="null"
              :id="idx"
              :checked="true"
            />
          </div>
          <div
            v-if="!isEditing(item, 'name')"
            class=" label-wrapper"
          >
            <span class="caption truncate-text v-align-super">
              {{ item.name }}
            </span>
            <i
              class="icon yule-icon-edit edit action-icon"
              @click="setEditing(item, 'name')"
            />
          </div>
          <div
            v-else
            class="input label-wrapper"
          >
            <input
              type="text"
              ref="editing"
              v-model="internalCollection[idx].name"
              @blur="removeEditing"
            />
          </div>
          <div
            v-if="!isEditing(item, 'value') || limitEditing"
            class=" value-wrapper"
          >
            <span class="caption truncate-text v-align-super">
              {{ item.value }}
            </span>
            <i
              v-if="!limitEditing"
              class="icon yule-icon-edit edit action-icon"
              @click="setEditing(item, 'value')"
            />
          </div>
          <div
            v-else
            class=" value-wrapper"
          >
            <input
              type="text"
              ref="editing"
              v-model="internalCollection[idx].value"
              @blur="removeEditing"
            />
          </div>
          <div class=" delete">
            <i
              v-if="!limitEditing"
              class="icon yule-icon-trash action-icon"
              @click="deleteElement(item)"
            />
          </div>
        </div>
      </Draggable>
    </drag-container>
  </div>
</template>
<script>
import Field from '@/components/helpers/DataInput/Field'
import { Draggable } from "vue-smooth-dnd"
import DragContainer from '@/components/helpers/DragNDrop/DragContainer'

export default {
  props: {
    limitEditing: {
      type: Boolean,
      default: false
    },
    collection: {
      type: Array,
      required: true
    },
    defaultValue: {
      required: false
    },
    element_type: {
      type: String
    }
  },
  components: {
    Field,
    DragContainer,
    Draggable
  },
  data() {
    return {
      internalCollection: [],
      internalDefaultValue: null,
      defaultValueIsArray: false,
      editing: null,
      editingAttr: null,
    }
  },
  computed: {
    dragIcon() {
      return require('@/assets/dragable-smaller.svg')
    },
  },
  mounted() {
    this.internalCollection = _.cloneDeep(this.collection)
    this.setupInternalDefaultValue()
  },
  methods: {
    isDefaultValue(item) {
      if (this.defaultValueIsArray) {
        return this.defaultValue.includes(item.value)
      } else {
        return this.defaultValue === item.value
      }
    },
    onDrop(dropResult) {    
      let droppedItem = dropResult.payload
      let startIdx    = this.internalCollection.indexOf(droppedItem)      
      // startIdx can be -1 if an item is dropped from another div      
      if (startIdx === -1) { return }
      let endIdx      = dropResult.addedIndex
      this.internalCollection.splice(startIdx, 1)
      this.internalCollection.splice(endIdx, 0, droppedItem)       
      this.updateOptions()
    },
    isEditing(item, attr) {
      if (!this.editing) { return false }
      return item == this.editing && attr == this.editingAttr
    },
    removeEditing() {
      this.editing = null
      this.updateOptions()
    },
    updateOptions() {
      this.$emit('input', this.internalCollection)
    },
    setEditing(item, attr) {
      this.editing     = item
      this.editingAttr = attr
    },
    deleteElement(item) {
      this.$emit('delete', item)
    },
    setEditingFocused() {
      this.$nextTick(() => {
        if (this.$refs.editing && this.$refs.editing.length > 0) {
          this.$refs.editing[0].focus()
        }
      })
    },
    setupInternalDefaultValue() {
      this.internalDefaultValue = this.defaultValue
      if (Array.isArray(this.internalDefaultValue)) {
        this.defaultValueIsArray = true
      }
    }
  },
  watch: {
    internalDefaultValue(val) {
      if (val !== this.defaultValue) {
        this.$emit('updateDefault', val)
      }
    },
    collection() {
      this.internalCollection = _.cloneDeep(this.collection)
    },
    editing() {
      this.setEditingFocused()
    },
    internalCollection: {
      deep: true,
      handler() {
        this.setEditingFocused()
      }
    },
    defaultValue(val) {
      this.setupInternalDefaultValue()
    }
  }
}
</script>
<style lang="sass" scoped>
.options-item-table-row
  display: table-row !important
  border-bottom: 1px solid #d4d4d5
  &:hover
    transition: all .15s ease-out
    background-color: #F9FAFB

.drag-wrapper
  width: 5%

.button-wrapper
  width: 7%
  margin-top: -4px

.label-wrapper
  width: 48%
  display: flex
  align-items: center

.value-wrapper
  width: 30%
  display: flex
  align-items: center

.edit
  cursor: pointer

.delete
  cursor: pointer
  width: 10%

.options-drag
  cursor: grab

.drag-icon
  pointer-events: none

.options-head
  display: flex
  border-bottom: 2px solid $ink-lighter
  padding: 0px 10px

.options-table
  margin: 30px 0
  width: 100%

.options-table-content
  justify-content: start
  align-items: center
  display: flex
  width: 100%
  padding: 12px 10px 8px 10px
  input
    border: 2px solid $sensual
    border-radius: 3px
    padding: 8px 5px
    width: auto
    font-size: 12px

.options-heading
  text-align: left
  padding: 10px 0

.name
  width: 55%

.max-height
  height: 100%

.drag-item
  display: flex !important
  background-color: #FFF
  border-bottom: 2px solid $sensual

.option-row
  border-bottom: 1px solid $paper
  cursor: grab

.active-item-content
  .options-container
    .drag-container
      margin-bottom: 20px !important
</style>



// WEBPACK FOOTER //
// DragEditorItemOptions.vue?e5a92b00